// Proofread Bucket — capture text selections for AI proofreading.
//
// Stores captured items, formats them for AI proofreading with instructions,
// parses AI replies with 2-3 options per item, and provides word-level diff.
// Items are persisted to localStorage (or PCPDesk bridge if present).

import { buildTokenizer } from "phi_tokenizer"
import { loadPlan } from "plan_storage"

// ─────────────────────────────────────────────────────────────────────────────
// Marker format (shared with task 016)
// ─────────────────────────────────────────────────────────────────────────────
// Delimiters for clipboard export: wraps each captured item so the AI reply
// can be parsed back to the original items.
export const MARKER_OPEN  = "⟦PB:"
export const MARKER_CLOSE = "⟧"
export const MARKER_END_OPEN = "⟦/PB:"

// Storage key for browser/PCPDesk persistence.
export const STORAGE_KEY = "pcp.proofreadBucket.v1"

// ─────────────────────────────────────────────────────────────────────────────
// Bucket store
// ─────────────────────────────────────────────────────────────────────────────

// Generate a short unique id for a bucket item.
function newId() {
  return "pb-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8)
}

// Read raw JSON from storage; returns null on missing/corrupt.
function readRaw() {
  try {
    const bridge = typeof window !== "undefined" && window.__PCP_LIBRARY__
    if (bridge && typeof bridge.proofreadBucketGet === "function") {
      return bridge.proofreadBucketGet()
    }
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw || null
  } catch (_) {
    return null
  }
}

// Write raw JSON to storage.
function writeRaw(json) {
  try {
    const bridge = typeof window !== "undefined" && window.__PCP_LIBRARY__
    if (bridge && typeof bridge.proofreadBucketSet === "function") {
      bridge.proofreadBucketSet(json)
      return
    }
    if (json) {
      localStorage.setItem(STORAGE_KEY, json)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch (_) {
    // quota/private-mode — fail silently
  }
}

// Load bucket items from storage. Tolerates missing/corrupt data → returns [].
export function bucketLoad() {
  const raw = readRaw()
  if (!raw) return []
  try {
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr : []
  } catch (_) {
    return []
  }
}

// Save bucket items to storage.
export function bucketSave(items) {
  if (!Array.isArray(items) || items.length === 0) {
    writeRaw(null)
  } else {
    writeRaw(JSON.stringify(items))
  }
}

// Add an item to the bucket. Returns the new item with generated id.
// Item shape: { id, sectionKey, fieldPath, start, end, original, createdAt, status }
export function bucketAdd(item) {
  const items = bucketLoad()
  const entry = {
    id: newId(),
    sectionKey: item.sectionKey || "",
    fieldPath: item.fieldPath || "",
    start: typeof item.start === "number" ? item.start : 0,
    end: typeof item.end === "number" ? item.end : 0,
    original: item.original || "",
    createdAt: new Date().toISOString(),
    status: "open",
  }
  items.push(entry)
  bucketSave(items)
  return entry
}

// Remove an item by id.
export function bucketRemove(id) {
  const items = bucketLoad().filter((it) => it.id !== id)
  bucketSave(items)
}

// Update an item's status (e.g., "open" → "resolved").
export function bucketUpdateStatus(id, status) {
  const items = bucketLoad()
  const item = items.find((it) => it.id === id)
  if (item) {
    item.status = status
    bucketSave(items)
  }
}

// Clear all resolved items.
export function bucketClearResolved() {
  const items = bucketLoad().filter((it) => it.status !== "resolved")
  bucketSave(items)
}

// Clear all items.
export function bucketClearAll() {
  bucketSave([])
}

// ─────────────────────────────────────────────────────────────────────────────
// Clipboard formatting
// ─────────────────────────────────────────────────────────────────────────────

// Format items for clipboard export. Each item is wrapped with markers:
//   ⟦PB:<id>⟧ <tokenized-text> ⟦/PB:<id>⟧
// Items are output in capture order.
export function formatForClipboard(items, plan) {
  if (!items || !items.length) return ""
  const tokenize = buildTokenizer(plan || loadPlan())
  return items
    .map((it) => {
      const text = tokenize(it.original || "")
      return `${MARKER_OPEN}${it.id}${MARKER_CLOSE} ${text} ${MARKER_END_OPEN}${it.id}${MARKER_CLOSE}`
    })
    .join("\n\n")
}

// ─────────────────────────────────────────────────────────────────────────────
// Prompt builder
// ─────────────────────────────────────────────────────────────────────────────

// Instruction block for AI proofreading
const PROMPT_INSTRUCTIONS = `Proofread the following text items for clarity, grammar, and person-centred tone.

For each item, provide 2–3 alternative rewrite options.

IMPORTANT:
- Keep the ⟦PB:…⟧ … ⟦/PB:…⟧ markers exactly as given.
- Keep any [Tokens] (e.g. [FirstName], [LastName], [Age]) untouched.
- Inside each marker pair, separate options with lines starting 1. 2. 3. or ---
- Output ONLY the rewritten marker blocks, nothing else.

`

// Build the full prompt for the clipboard: instruction block + marked items.
export function buildPrompt(items, plan) {
  if (!items || !items.length) return ""
  return PROMPT_INSTRUCTIONS + formatForClipboard(items, plan)
}

// ─────────────────────────────────────────────────────────────────────────────
// Reply parsing
// ─────────────────────────────────────────────────────────────────────────────

// Parse the AI reply to extract corrections for each item.
// Returns: { [id]: { options: string[] } }
// Tolerates code fences, prose around blocks, missing items, and 1-3 options.
// Options inside each marker pair are separated by lines starting with `1.`, `2.`, `3.` or `---`.
export function parseReply(text) {
  if (!text || typeof text !== "string") return {}

  // Strip code fences (```...```) if present
  let cleaned = text.replace(/```[\s\S]*?```/g, (match) => {
    // Extract content inside fences
    return match.replace(/^```[^\n]*\n?/, "").replace(/\n?```$/, "")
  })

  const result = {}

  // Match marker pairs: ⟦PB:<id>⟧ ... ⟦/PB:<id>⟧
  // Use a regex to find all marker pairs
  const markerRegex = new RegExp(
    escapeRegex(MARKER_OPEN) + "([^" + escapeRegex(MARKER_CLOSE) + "]+)" + escapeRegex(MARKER_CLOSE) +
    "([\\s\\S]*?)" +
    escapeRegex(MARKER_END_OPEN) + "\\1" + escapeRegex(MARKER_CLOSE),
    "g"
  )

  let match
  while ((match = markerRegex.exec(cleaned)) !== null) {
    const id = match[1].trim()
    const content = match[2].trim()

    if (!id) continue

    // Parse options from the content
    // Options can be separated by:
    //   - Lines starting with 1. 2. 3.
    //   - Lines starting with ---
    const options = parseOptions(content)

    result[id] = { options }
  }

  return result
}

// Escape special regex characters
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

// Parse options from content inside a marker pair.
// Options can be separated by numbered lines (1. 2. 3.) or --- separators.
function parseOptions(content) {
  if (!content) return []

  // Check if content has numbered options (1. 2. 3.)
  const numberedPattern = /^(\d+)\.\s*/m
  const hasSeparators = /^---+\s*$/m.test(content)

  let parts = []

  if (numberedPattern.test(content)) {
    // Split by numbered lines
    parts = content.split(/(?:^|\n)(?=\d+\.\s)/g).filter(Boolean)
    parts = parts.map(p => p.replace(/^\d+\.\s*/, "").trim()).filter(Boolean)
  } else if (hasSeparators) {
    // Split by --- separators
    parts = content.split(/^---+\s*$/m).map(p => p.trim()).filter(Boolean)
  } else {
    // Single option (the whole content)
    const trimmed = content.trim()
    if (trimmed) parts = [trimmed]
  }

  // Limit to 3 options
  return parts.slice(0, 3)
}

// ─────────────────────────────────────────────────────────────────────────────
// Word-level diff
// ─────────────────────────────────────────────────────────────────────────────

// Compute a word-level diff between original and revised text.
// Returns an array of spans: { type: "equal"|"delete"|"insert", text: string }
// Uses a simple LCS-based approach suitable for short text.
export function wordDiff(original, revised) {
  const origWords = tokenizeWords(original)
  const revWords = tokenizeWords(revised)

  // Compute LCS
  const lcs = longestCommonSubsequence(origWords, revWords)

  // Build diff spans
  const spans = []
  let oi = 0, ri = 0, li = 0

  while (oi < origWords.length || ri < revWords.length) {
    if (li < lcs.length && oi < origWords.length && ri < revWords.length &&
        origWords[oi] === lcs[li] && revWords[ri] === lcs[li]) {
      // Common word
      pushSpan(spans, "equal", origWords[oi])
      oi++; ri++; li++
    } else {
      // Deletions from original
      while (oi < origWords.length && (li >= lcs.length || origWords[oi] !== lcs[li])) {
        pushSpan(spans, "delete", origWords[oi])
        oi++
      }
      // Insertions in revised
      while (ri < revWords.length && (li >= lcs.length || revWords[ri] !== lcs[li])) {
        pushSpan(spans, "insert", revWords[ri])
        ri++
      }
    }
  }

  return mergeSpans(spans)
}

// Tokenize text into words and whitespace preserving all characters.
function tokenizeWords(text) {
  if (!text) return []
  // Split into words and whitespace/punctuation, keeping everything
  return text.match(/\S+|\s+/g) || []
}

// Longest Common Subsequence of two arrays
function longestCommonSubsequence(a, b) {
  const m = a.length, n = b.length
  // DP table
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
      }
    }
  }

  // Backtrack to find LCS
  const lcs = []
  let i = m, j = n
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      lcs.unshift(a[i - 1])
      i--; j--
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--
    } else {
      j--
    }
  }

  return lcs
}

// Push a span, merging with previous if same type
function pushSpan(spans, type, text) {
  if (spans.length > 0 && spans[spans.length - 1].type === type) {
    spans[spans.length - 1].text += text
  } else {
    spans.push({ type, text })
  }
}

// Merge consecutive spans of the same type
function mergeSpans(spans) {
  const merged = []
  for (const span of spans) {
    if (merged.length > 0 && merged[merged.length - 1].type === span.type) {
      merged[merged.length - 1].text += span.text
    } else {
      merged.push({ ...span })
    }
  }
  return merged
}
